/**
 * odl_app cross-entity reference helpers.
 *
 * The REST contract uses { type, id, name } envelopes for every cross-entity
 * reference (owners, customers, load types, etc.). These helpers provide
 * display-label, route, and lazy-fetch utilities so every callsite handles
 * refs the same way.
 */

import { skipToken } from '@reduxjs/toolkit/query';

import { resolveRefRoute } from '@sophocles/router/entityRedirectTarget';

import { entitiesApi } from './entitiesApi';

/** Plural route segment for each known resource type. */
const PLURAL = {
  'site-group': 'site-groups',
  customer: 'customers',
  user: 'users',
  camera: 'cameras',
  radio: 'radios',
  site: 'sites',
};

/**
 * Returns the display label for a reference object.
 *
 * Site-group refs carry `name` and `description` as separate keys; the
 * "name / description" display label is composed here, client-side, for
 * every chip/label callsite.
 *
 * @param {{ type?: string, name?: string, description?: string } | null | undefined} ref
 * @returns {string}
 */
export const refLabel = ref => {
  if (!ref) return '';
  if (ref.type === 'site-group' && ref.description) {
    return `${ref.name} / ${ref.description}`;
  }
  return ref.name ?? '';
};

/**
 * Returns the front-end navigation route for a reference object, or null
 * when the type has no registered destination (unlinked chip).
 *
 * @param {{ type: string, id: string|number } | null | undefined} ref
 * @returns {string | null}
 */
export const refRoute = ref => {
  if (!ref) return null;
  return resolveRefRoute(ref.type, ref.id);
};

/**
 * Lazily fetches the full entity for a reference when more than the label is
 * needed. Skips the request when ref is falsy or the type has no retrieve
 * endpoint registered on entitiesApi.
 *
 * Returns the same shape as RTK Query hooks: { data, isLoading, isError }.
 *
 * @param {{ type: string, id: string|number } | null | undefined} ref
 */
export function useResolveRef(ref) {
  // Build the resource path from the ref type, then resolve via the generic
  // retrieveEntity endpoint. skipToken suppresses the request when ref is absent.
  const path = ref ? `${PLURAL[ref.type] ?? `${ref.type}s`}/${ref.id}/` : null;
  return entitiesApi.endpoints.retrieveEntity.useQuery(path ? { path } : skipToken);
}
